import {
  ActionMode,
  ActionSource,
  getSelectionsOfType,
  Hover,
  HoverType,
  Selection,
  SelectionType,
} from 'app/modules/editor/model/actionmode';
import {
  ClipPathLayer,
  GroupLayer,
  Layer,
  LayerUtil,
  PathLayer,
  VectorLayer,
} from 'app/modules/editor/model/layers';
import { Command, HitOptions, HitResult, Path, SubPath } from 'app/modules/editor/model/paths';
import { MathUtil, Matrix, Point } from 'app/modules/editor/scripts/common';
import { getContext2d } from 'app/modules/editor/scripts/dom';
import { DestroyableMixin } from 'app/modules/editor/scripts/mixins';
import {
  ActionModeService,
  LayerTimelineService,
  ShortcutService,
} from 'app/modules/editor/services';
import { State, Store } from 'app/modules/editor/store';
import {
  getActionMode,
  getActionModeEndState,
  getActionModeHover,
  getActionModeStartState,
} from 'app/modules/editor/store/actionmode/selectors';
import { getCanvasOverlayState } from 'app/modules/editor/store/common/selectors';
import { getAnimatedVectorLayer } from 'app/modules/editor/store/playback/selectors';
import { flatMap, remove, uniq } from 'lodash-es';
import { combineLatest, of } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { CanvasCamera } from './CanvasCamera';
import { hitTestLayer } from './LayerGeometry';
import type { CanvasPreview } from './CanvasPreview';
import * as CanvasUtil from './CanvasUtil';
import { PairSubPathHelper } from './PairSubPathHelper';
import { SegmentSplitter } from './SegmentSplitter';
import { SelectionHelper } from './SelectionHelper';
import { ShapeSplitter } from './ShapeSplitter';

// The line width of a highlight in css pixels.
const HIGHLIGHT_LINE_WIDTH = 6;
// The line dash of a highlight in css pixels.
const HIGHLIGHT_LINE_DASH = 12;
// The minimum distance between a point and a path that causes a snap.
const MIN_SNAP_THRESHOLD = 12;
// The distance of a mouse gesture that triggers a drag, in css pixels.
const DRAG_TRIGGER_TOUCH_SLOP = 6;
// The radius of a medium point in css pixels.
const MEDIUM_POINT_RADIUS = 8;
// The radius of a small point in css pixels.
const SMALL_POINT_RADIUS = MEDIUM_POINT_RADIUS / 1.7;

const SPLIT_POINT_RADIUS_FACTOR = 0.8;
const SELECTED_POINT_RADIUS_FACTOR = 1.25;
const POINT_BORDER_FACTOR = 1.075;

const NORMAL_POINT_COLOR = '#2962FF'; // Blue A700
// Deep orange 900. Darkened from orange 900 (#E65100, 3.79:1) so the white point number reaches
// 4.5:1 (5.60:1). Split points also get a ring (see executeLabeledPoint), so they don't rely on
// color alone.
const SPLIT_POINT_COLOR = '#BF360C';
const HIGHLIGHT_COLOR = '#448AFF';
const POINT_BORDER_COLOR = '#000';
const POINT_TEXT_COLOR = '#fff';
// Red 800. Darkened from red 500 (#F44336, 3.68:1) so the white point number reaches 4.5:1
// (5.62:1).
const ERROR_COLOR = '#C62828';

const NO_HIT: HitResult = {
  isHit: false,
  isEndPointHit: false,
  isSegmentHit: false,
  isShapeHit: false,
  endPointHits: [],
  segmentHits: [],
  shapeHits: [],
};

// TODO: make shape shifter mode work with clip paths
// TODO: make segment splitter work with trim paths
// TODO: make trim paths work with shifts/reversals
// TODO: make cursor 'drag' in selection mode when dragging points
// TODO: need to avoid cases where the pathData could be undefined
// (i.e. this could happen if the user enters action mode w/o setting a path string on the layer)

type Context = CanvasRenderingContext2D;

/**
 * Draws overlay selections and other content on top of the currently active vector layer.
 */
export class CanvasOverlay extends DestroyableMixin() {
  private camera: CanvasCamera | undefined;
  // The canvas editor draws selected layers its own way.
  private showsLayerSelections = true;
  vectorLayer: VectorLayer | undefined;
  // Normal mode variables.
  private hiddenLayerIds: ReadonlySet<string> = new Set<string>();
  private selectedLayerIds: ReadonlySet<string> = new Set<string>();
  // Shape Shifter mode variables.
  private blockLayerId: string | undefined;
  actionMode = ActionMode.None;
  private actionHover: Hover | undefined;
  actionSelections: ReadonlyArray<Selection> = [];
  private currentHoverPreviewPath: Path | undefined;
  pairedSubPaths = new Set<number>();
  unpairedSubPath: { source: ActionSource; subIdx: number } | undefined;
  private isActionMode = false;
  private selectedBlockLayerIds: ReadonlySet<string> = new Set<string>();
  private subIdxWithError: number | undefined;

  private selectionHelper: SelectionHelper | undefined;
  private pairSubPathHelper: PairSubPathHelper | undefined;
  private segmentSplitter: SegmentSplitter | undefined;
  private shapeSplitter: ShapeSplitter | undefined;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    readonly actionSource: ActionSource,
    readonly store: Store<State>,
    readonly actionModeService: ActionModeService,
    private readonly layerTimelineService: LayerTimelineService,
    // Edits in progress, which only the animated canvas shows.
    private readonly preview?: CanvasPreview,
  ) {
    super();
  }

  init() {
    if (this.actionSource === ActionSource.Animated) {
      // Animated canvas specific setup.
      const { preview } = this;
      this.registerSubscription(
        combineLatest([
          this.store.select(getAnimatedVectorLayer),
          this.store.select(getCanvasOverlayState),
          preview ? preview.asObservable().pipe(startWith(undefined)) : of(undefined),
        ]).subscribe(
          ([
            { vl, currentTime },
            { hiddenLayerIds, selectedLayerIds, isActionMode, selectedBlockLayerIds },
          ]) => {
            // Selections and hit tests follow the edit too.
            this.vectorLayer = preview ? preview.apply(vl, currentTime) : vl;
            this.hiddenLayerIds = hiddenLayerIds;
            this.selectedLayerIds = selectedLayerIds;
            this.isActionMode = isActionMode;
            this.selectedBlockLayerIds = selectedBlockLayerIds;
            this.draw();
          },
        ),
      );
    } else {
      // From & to canvas specific setup.
      const shapeShifterSelector =
        this.actionSource === ActionSource.From ? getActionModeStartState : getActionModeEndState;
      this.registerSubscription(
        this.store
          .select(shapeShifterSelector)
          .subscribe(
            ({
              vectorLayer,
              blockLayerId,
              isActionMode,
              hover,
              selections,
              pairedSubPaths,
              unpairedSubPath,
              hiddenLayerIds,
              selectedLayerIds,
              subIdxWithError,
            }) => {
              const previousPath = this.activePath;
              this.vectorLayer = vectorLayer;
              this.blockLayerId = blockLayerId;
              this.isActionMode = isActionMode;
              this.actionHover = hover;
              this.actionSelections = selections;
              this.pairedSubPaths = new Set(pairedSubPaths);
              this.unpairedSubPath = unpairedSubPath;
              this.hiddenLayerIds = hiddenLayerIds;
              this.selectedLayerIds = selectedLayerIds;
              this.subIdxWithError = subIdxWithError;
              if (this.activePath !== previousPath) {
                // The helpers only update on mouse events, so after an undo (for example) they
                // would still point at subpaths and commands the path no longer has. The hover
                // preview was built from the old path too.
                this.selectionHelper?.reset();
                this.segmentSplitter?.reset();
                this.shapeSplitter?.reset();
                this.updateHoverPreview(hover);
              }
              this.draw();
            },
          ),
      );
      this.registerSubscription(
        this.store.select(getActionMode).subscribe(mode => {
          this.actionMode = mode;
          const layer = this.activePathLayer;
          if (
            this.actionMode === ActionMode.SplitCommands ||
            (this.actionMode === ActionMode.SplitSubPaths &&
              layer &&
              layer.isStroked() &&
              !layer.isFilled())
          ) {
            const subIdxs = new Set<number>();
            for (const s of this.actionSelections) {
              subIdxs.add(s.subIdx);
            }
            this.segmentSplitter = new SegmentSplitter(this);
          } else {
            this.segmentSplitter = undefined;
          }
          this.selectionHelper =
            this.actionMode === ActionMode.Selection ? new SelectionHelper(this) : undefined;
          if (this.actionMode === ActionMode.PairSubPaths) {
            this.pairSubPathHelper = new PairSubPathHelper(this);
            const selections = this.actionSelections.filter(s => s.type === SelectionType.SubPath);
            if (selections.length) {
              const { source, subIdx } = selections[0];
              // TODO: avoid calling this in a subscription (should automatically do this)
              this.actionModeService.setUnpairedSubPath({ source, subIdx });
            }
          } else {
            this.pairSubPathHelper = undefined;
          }
          this.shapeSplitter =
            this.actionMode === ActionMode.SplitSubPaths && layer && layer.isFilled()
              ? new ShapeSplitter(this)
              : undefined;
          this.currentHoverPreviewPath = undefined;
          this.draw();
        }),
      );
      // TODO: avoid re-executing the draw by combining with the above subscriptions
      this.registerSubscription(
        this.store.select(getActionModeHover).subscribe(hover => {
          this.updateHoverPreview(hover);
          this.draw();
        }),
      );
    }
  }

  private updateHoverPreview(hover: Hover | undefined) {
    let previewPath: Path | undefined;
    if (
      this.vectorLayer &&
      this.activePath &&
      hover &&
      hover.source === this.actionSource &&
      hover.cmdIdx !== undefined
    ) {
      // If the user is hovering over the inspector split button, then build
      // a snapshot of what the path would look like after the action
      // and display the result.
      const mutator = this.activePath.mutate();
      const { type, subIdx, cmdIdx } = hover;
      switch (type) {
        case HoverType.Split:
          previewPath = mutator.splitCommandInHalf(subIdx, cmdIdx).build();
          break;
        case HoverType.Unsplit:
          previewPath = mutator.unsplitCommand(subIdx, cmdIdx).build();
          break;
      }
    }
    this.currentHoverPreviewPath = previewPath;
  }

  private get overlayCtx() {
    return getContext2d(this.canvas);
  }

  // CSS pixels per viewport unit.
  private get cssScale() {
    return this.camera?.scale ?? 1;
  }

  private get highlightLineWidth() {
    return HIGHLIGHT_LINE_WIDTH / this.cssScale;
  }

  private get minSnapThreshold() {
    return MIN_SNAP_THRESHOLD / this.cssScale;
  }

  private get highlightLineDash() {
    return [HIGHLIGHT_LINE_DASH / this.cssScale, HIGHLIGHT_LINE_DASH / this.cssScale];
  }

  private get smallPointRadius() {
    return SMALL_POINT_RADIUS / this.cssScale;
  }

  private get mediumPointRadius() {
    return MEDIUM_POINT_RADIUS / this.cssScale;
  }

  private get splitPointRadius() {
    return this.mediumPointRadius * SPLIT_POINT_RADIUS_FACTOR;
  }

  private get selectedSegmentLineWidth() {
    return HIGHLIGHT_LINE_WIDTH / this.cssScale / 1.9;
  }

  private get unselectedSegmentLineWidth() {
    return HIGHLIGHT_LINE_WIDTH / this.cssScale / 3;
  }

  get dragTriggerTouchSlop() {
    return DRAG_TRIGGER_TOUCH_SLOP / this.cssScale;
  }

  // NOTE: only use this for action mode
  get activePathLayer() {
    if (!this.vectorLayer || !this.blockLayerId) {
      return undefined;
    }
    const layer = this.vectorLayer.findLayerById(this.blockLayerId);
    return layer instanceof PathLayer || layer instanceof ClipPathLayer ? layer : undefined;
  }

  // NOTE: only use this for action mode
  get activePath() {
    const layer = this.activePathLayer;
    if (!layer) {
      return undefined;
    }
    return layer.pathData;
  }

  // Stroked paths that aren't filled are split by segment, and all other paths by shape.
  private get subPathSplitter() {
    const layer = this.activePathLayer;
    if (!layer) {
      return undefined;
    }
    return !layer.isFilled() && layer.isStroked() ? this.segmentSplitter : this.shapeSplitter;
  }

  setCamera(camera: CanvasCamera) {
    this.camera = camera;
    CanvasUtil.setCanvasSize(this.canvas, camera);
    this.draw();
  }

  /** Whether to highlight the selected layers, which the canvas editor does on its own. */
  setShowsLayerSelections(show: boolean) {
    this.showsLayerSelections = show;
    this.draw();
  }

  draw() {
    const { camera } = this;
    if (!camera) {
      return;
    }
    const ctx = this.overlayCtx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.save();
    // The overlay covers the whole panel, but only draws on the artboard.
    CanvasUtil.clipToArtboard(ctx, camera);
    if (this.vectorLayer) {
      ctx.save();
      const { a, b, c, d, e, f } = camera.getDeviceTransform();
      ctx.setTransform(a, b, c, d, e, f);
      this.drawLayerSelections(ctx, this.vectorLayer, this.vectorLayer);
      this.drawHighlights(ctx);
      ctx.restore();
      // Draw points in terms of physical pixels, not viewport pixels.
      this.drawLabeledPoints(ctx, camera);
      this.drawDraggingPoints(ctx, camera);
      this.drawFloatingPreviewPoint(ctx, camera);
      this.drawFloatingSplitFilledPathPreviewPoints(ctx, camera);
    }
    this.drawPixelGrid(ctx, camera);
    ctx.restore();
  }

  // Recursively draws all layer selections to the canvas.
  private drawLayerSelections(ctx: Context, root: VectorLayer, curr: Layer) {
    if (this.isActionMode) {
      // Don't draw selections for hidden layers or while in action mode.
      return;
    }
    if (
      (this.showsLayerSelections && this.selectedLayerIds.has(curr.id)) ||
      this.selectedBlockLayerIds.has(curr.id)
    ) {
      const flattenedTransform = LayerUtil.getCanvasTransformForLayer(root, curr.id);
      if (curr instanceof ClipPathLayer) {
        if (curr.pathData && curr.pathData.getCommands().length) {
          CanvasUtil.executeCommands(ctx, curr.pathData.getCommands(), flattenedTransform);
          executeHighlights(ctx, HIGHLIGHT_COLOR, this.highlightLineWidth, this.highlightLineDash);
          ctx.clip();
        }
      } else if (curr instanceof PathLayer) {
        if (curr.pathData && curr.pathData.getCommands().length) {
          ctx.save();
          CanvasUtil.executeCommands(ctx, curr.pathData.getCommands(), flattenedTransform);
          executeHighlights(ctx, HIGHLIGHT_COLOR, this.highlightLineWidth);
          ctx.restore();
        }
      } else if (curr instanceof VectorLayer || curr instanceof GroupLayer) {
        const bounds = curr.bounds;
        if (bounds) {
          ctx.save();
          const { a, b, c, d, e, f } = flattenedTransform;
          ctx.transform(a, b, c, d, e, f);
          ctx.beginPath();
          ctx.rect(bounds.l, bounds.t, bounds.r - bounds.l, bounds.b - bounds.t);
          executeHighlights(ctx, HIGHLIGHT_COLOR, this.highlightLineWidth);
          ctx.restore();
        }
      }
    }
    curr.children.forEach(child => this.drawLayerSelections(ctx, root, child));
  }

  // Draw any highlighted segments.
  private drawHighlights(ctx: Context) {
    const activePath = this.activePath;
    if (
      !this.isActionMode ||
      this.actionSource === ActionSource.Animated ||
      !this.vectorLayer ||
      !this.blockLayerId ||
      !activePath
    ) {
      return;
    }

    const flattenedTransform = LayerUtil.getCanvasTransformForLayer(
      this.vectorLayer,
      this.blockLayerId,
    );
    const currentHover = this.actionHover;

    if (this.selectionHelper) {
      // Draw any highlighted subpaths. We'll highlight a subpath if a subpath
      // selection or a point selection exists.
      const selectedSubPaths = uniq(
        (this.actionSelections as Selection[])
          .filter(s => {
            return (
              s.source === this.actionSource &&
              (s.type === SelectionType.Point || s.type === SelectionType.SubPath)
            );
          })
          .map(s => s.subIdx),
      )
        .map(subIdx => activePath.getSubPath(subIdx))
        .filter(subPath => !subPath.isCollapsing());

      for (const subPath of selectedSubPaths) {
        // If the subpath has a split segment, highlight it in orange. Otherwise,
        // use the default blue highlight color.
        const isSplitSubPath = subPath.getCommands().some(c => c.isSplitSegment());
        const highlightColor = isSplitSubPath ? SPLIT_POINT_COLOR : HIGHLIGHT_COLOR;
        CanvasUtil.executeCommands(ctx, subPath.getCommands(), flattenedTransform);
        executeHighlights(ctx, highlightColor, this.selectedSegmentLineWidth);
      }

      const segmentSelections = getSelectionsOfType(this.actionSelections, SelectionType.Segment)
        .filter(s => s.source === this.actionSource)
        .map(s => {
          return { subIdx: s.subIdx, cmdIdx: s.cmdIdx };
        });
      const hover = currentHover;
      if (
        hover &&
        hover.source === this.actionSource &&
        hover.type === HoverType.Segment &&
        hover.cmdIdx !== undefined
      ) {
        segmentSelections.push({
          subIdx: hover.subIdx,
          cmdIdx: hover.cmdIdx,
        });
      }
      const segmentSelectionCmds = segmentSelections
        .map(s => activePath.getCommand(s.subIdx, s.cmdIdx))
        .filter(cmd => cmd.isSplitSegment());
      CanvasUtil.executeCommands(ctx, segmentSelectionCmds, flattenedTransform);
      executeHighlights(ctx, SPLIT_POINT_COLOR, this.selectedSegmentLineWidth);

      // Highlight any subpaths with errors.
      if (this.subIdxWithError !== undefined) {
        const cmds = activePath.getSubPath(this.subIdxWithError).getCommands();
        CanvasUtil.executeCommands(ctx, cmds, flattenedTransform);
        executeHighlights(ctx, ERROR_COLOR, this.highlightLineWidth, this.highlightLineDash);
      }
    } else if (this.segmentSplitter) {
      // Highlight the segment as the user hovers over it.
      const projectionOntoPath = this.segmentSplitter.getProjectionOntoPath();
      if (projectionOntoPath && projectionOntoPath.projection.d < this.minSnapThreshold) {
        const { subIdx, cmdIdx } = projectionOntoPath;
        CanvasUtil.executeCommands(
          ctx,
          [activePath.getCommand(subIdx, cmdIdx)],
          flattenedTransform,
        );
        executeHighlights(ctx, SPLIT_POINT_COLOR, this.selectedSegmentLineWidth);
      }
    }

    // Draw any existing split shape segments to the canvas.
    const commands = (activePath.getSubPaths() as SubPath[])
      .filter(s => !s.isCollapsing())
      .flatMap(s => s.getCommands() as Command[])
      .filter(c => c.isSplitSegment());
    CanvasUtil.executeCommands(ctx, commands, flattenedTransform);
    executeHighlights(ctx, SPLIT_POINT_COLOR, this.unselectedSegmentLineWidth);

    if (this.pairSubPathHelper) {
      const currUnpair = this.unpairedSubPath;
      if (currUnpair) {
        // Draw the current unpaired subpath in orange, if it exists.
        const { source, subIdx } = currUnpair;
        if (source === this.actionSource) {
          const subPath = activePath.getSubPath(subIdx);
          CanvasUtil.executeCommands(ctx, subPath.getCommands(), flattenedTransform);
          executeHighlights(ctx, SPLIT_POINT_COLOR, this.selectedSegmentLineWidth);
        }
      }
      // Copy the set, since the hovered subpath is removed from it below.
      const pairedSubPaths = new Set(this.pairedSubPaths);
      const hasHover =
        currentHover &&
        currentHover.source === this.actionSource &&
        currentHover.type === HoverType.SubPath;
      if (hasHover) {
        pairedSubPaths.delete(currentHover.subIdx);
      }
      if (pairedSubPaths.size) {
        // Draw any already paired subpaths in blue.
        const pairedCmds = flatMap(
          Array.from(pairedSubPaths),
          subIdx => activePath.getSubPath(subIdx).getCommands() as Command[],
        );
        CanvasUtil.executeCommands(ctx, pairedCmds, flattenedTransform);
        executeHighlights(ctx, NORMAL_POINT_COLOR, this.selectedSegmentLineWidth);
      }
      if (hasHover) {
        // Highlight the hover in orange, if it exists.
        const hoverCmds = activePath.getSubPath(currentHover.subIdx).getCommands();
        CanvasUtil.executeCommands(ctx, hoverCmds, flattenedTransform);
        executeHighlights(ctx, SPLIT_POINT_COLOR, this.selectedSegmentLineWidth);
      }
    } else if (this.shapeSplitter) {
      // If we are splitting a filled subpath, draw the in progress drag segment.
      const proj1 = this.shapeSplitter.getInitialProjectionOntoPath();
      const proj2 = this.shapeSplitter.getFinalProjectionOntoPath();
      if (proj1) {
        // Draw a line from the starting projection to the final projection (or
        // to the last known mouse location, if one doesn't exist).
        const startPoint = applyGroupTransform(proj1.projection, flattenedTransform);
        const endPoint = proj2
          ? applyGroupTransform(proj2.projection, flattenedTransform)
          : this.shapeSplitter.getLastKnownMouseLocation();
        if (endPoint) {
          ctx.beginPath();
          ctx.moveTo(startPoint.x, startPoint.y);
          ctx.lineTo(endPoint.x, endPoint.y);
          executeHighlights(ctx, SPLIT_POINT_COLOR, this.selectedSegmentLineWidth);
        }
      }
      if (!proj1 || proj2) {
        // Highlight the segment as the user hovers over it.
        const projectionOntoPath = this.shapeSplitter.getCurrentProjectionOntoPath();
        if (projectionOntoPath) {
          const projection = projectionOntoPath.projection;
          if (projection && projection.d < this.minSnapThreshold) {
            const { subIdx, cmdIdx } = projectionOntoPath;
            CanvasUtil.executeCommands(
              ctx,
              [activePath.getCommand(subIdx, cmdIdx)],
              flattenedTransform,
            );
            executeHighlights(ctx, SPLIT_POINT_COLOR, this.selectedSegmentLineWidth);
          }
        }
      }
    }
  }

  // Draw any labeled points.
  private drawLabeledPoints(ctx: Context, camera: CanvasCamera) {
    const activePath = this.activePath;
    if (
      !this.isActionMode ||
      this.actionSource === ActionSource.Animated ||
      !this.vectorLayer ||
      !this.blockLayerId ||
      !activePath
    ) {
      return;
    }

    let path = activePath;
    if (this.currentHoverPreviewPath) {
      path = this.currentHoverPreviewPath;
    }

    interface PointInfo {
      cmd: Command;
      subIdx: number;
      cmdIdx: number;
    }

    // Create a list of all path points in their normal order.
    const pointInfos = (path.getSubPaths() as SubPath[])
      .filter(s => !s.isCollapsing())
      .map((s, subIdx) => {
        return s.getCommands().map((cmd, cmdIdx) => {
          return { cmd, subIdx, cmdIdx } as PointInfo;
        });
      })
      .flatMap(pis => pis)
      .reverse();

    const subPathSelections = this.actionSelections.filter(s => s.type === SelectionType.SubPath);
    const pointSelections = this.actionSelections.filter(s => s.type === SelectionType.Point);

    // Remove all selected points from the list.
    const isPointInfoSelectedFn = ({ subIdx, cmdIdx }: PointInfo) => {
      return (
        subPathSelections.some(s => s.subIdx === subIdx) ||
        pointSelections.some(s => s.subIdx === subIdx && s.cmdIdx === cmdIdx)
      );
    };
    const selectedPointInfos = remove(pointInfos, pi => isPointInfoSelectedFn(pi));
    // Remove any subpath points that share the same subIdx as an existing selection.
    // We'll call these 'medium' points (i.e. labeled, but not selected), and we'll
    // always draw selected points on top of medium points, and medium points
    // on top of small points.
    const isPointInfoAtLeastMediumFn = ({ subIdx }: PointInfo) => {
      return (
        subPathSelections.some(s => s.subIdx === subIdx) ||
        pointSelections.some(s => s.subIdx === subIdx)
      );
    };
    pointInfos.push(...remove(pointInfos, pi => isPointInfoAtLeastMediumFn(pi)));
    pointInfos.push(...selectedPointInfos);

    const currentHover = this.actionHover;

    // Remove a hovering point, if one exists.
    const hoveringPointInfos = remove(pointInfos, ({ subIdx, cmdIdx }: PointInfo) => {
      const hover = currentHover;
      return (
        hover &&
        hover.type === HoverType.Point &&
        hover.subIdx === subIdx &&
        hover.cmdIdx === cmdIdx
      );
    });
    // Remove any subpath points that share the same subIdx as an existing hover.
    const isPointInfoHoveringFn = ({ subIdx }: PointInfo) => {
      const hover = currentHover;
      return hover && hover.type !== HoverType.Segment && hover.subIdx === subIdx;
    };
    // Similar to above, always draw hover points on top of subpath hover points.
    pointInfos.push(...remove(pointInfos, pi => isPointInfoHoveringFn(pi)));
    pointInfos.push(...hoveringPointInfos);

    const draggingIndex =
      this.selectionHelper && this.selectionHelper.isDragTriggered()
        ? this.selectionHelper.getDraggableSplitIndex()
        : undefined;

    for (const { cmd, subIdx, cmdIdx } of pointInfos) {
      if (draggingIndex && subIdx === draggingIndex.subIdx && cmdIdx === draggingIndex.cmdIdx) {
        // Skip the currently dragged point. We'll draw that next.
        continue;
      }
      let radius = this.smallPointRadius;
      let text: string | undefined;
      const isHovering = isPointInfoHoveringFn({ cmd, subIdx, cmdIdx });
      const isAtLeastMedium = isPointInfoAtLeastMediumFn({ cmd, subIdx, cmdIdx });
      if ((isAtLeastMedium || isHovering) && this.actionMode === ActionMode.Selection) {
        radius = this.mediumPointRadius * SELECTED_POINT_RADIUS_FACTOR;
        const isPointEnlargedFn = (source: ActionSource, sIdx: number, cIdx: number) => {
          return pointSelections.some(s => {
            return s.subIdx === sIdx && s.cmdIdx === cIdx && s.source === source;
          });
        };
        if (
          (isHovering && cmdIdx === currentHover?.cmdIdx) ||
          isPointEnlargedFn(ActionSource.From, subIdx, cmdIdx) ||
          isPointEnlargedFn(ActionSource.To, subIdx, cmdIdx)
        ) {
          radius /= SPLIT_POINT_RADIUS_FACTOR;
        }
        text = (cmdIdx + 1).toString();
      }
      let color: string;
      const isSplitPoint = cmd.isSplitPoint();
      if (isSplitPoint) {
        radius *= SPLIT_POINT_RADIUS_FACTOR;
        color = SPLIT_POINT_COLOR;
      } else {
        color = NORMAL_POINT_COLOR;
      }
      const flattenedTransform = LayerUtil.getCanvasTransformForLayer(
        this.vectorLayer,
        this.blockLayerId,
      );
      executeLabeledPoint(
        ctx,
        camera,
        applyGroupTransform(cmd.end, flattenedTransform),
        radius,
        color,
        text,
        isSplitPoint,
      );
    }
  }

  // Draw any actively dragged points along the path in selection mode.
  private drawDraggingPoints(ctx: Context, camera: CanvasCamera) {
    if (
      !this.isActionMode ||
      this.actionSource === ActionSource.Animated ||
      !this.vectorLayer ||
      !this.blockLayerId ||
      !this.activePath
    ) {
      return;
    }

    if (
      this.actionMode !== ActionMode.Selection ||
      !this.selectionHelper ||
      !this.selectionHelper.isDragTriggered()
    ) {
      return;
    }
    const flattenedTransform = LayerUtil.getCanvasTransformForLayer(
      this.vectorLayer,
      this.blockLayerId,
    );
    const projection = this.selectionHelper.getProjectionOntoPath()?.projection;
    if (!projection) {
      return;
    }
    const point =
      projection.d < this.minSnapThreshold
        ? applyGroupTransform(projection, flattenedTransform)
        : this.selectionHelper.getLastKnownMouseLocation();
    if (point) {
      executeLabeledPoint(
        ctx,
        camera,
        point,
        this.splitPointRadius,
        SPLIT_POINT_COLOR,
        undefined,
        true,
      );
    }
  }

  // Draw a floating point preview over the canvas in split commands mode
  // and split subpaths mode for stroked paths.
  private drawFloatingPreviewPoint(ctx: Context, camera: CanvasCamera) {
    if (
      !this.isActionMode ||
      this.actionSource === ActionSource.Animated ||
      !this.vectorLayer ||
      !this.blockLayerId ||
      !this.activePath
    ) {
      return;
    }

    const pathLayer = this.activePathLayer;
    const projection = this.segmentSplitter?.getProjectionOntoPath()?.projection;
    if (
      (this.actionMode !== ActionMode.SplitCommands &&
        this.actionMode !== ActionMode.SplitSubPaths &&
        !pathLayer?.isStroked()) ||
      !projection
    ) {
      return;
    }
    if (projection.d < this.minSnapThreshold) {
      const flattenedTransform = LayerUtil.getCanvasTransformForLayer(
        this.vectorLayer,
        this.blockLayerId,
      );
      executeLabeledPoint(
        ctx,
        camera,
        applyGroupTransform(projection, flattenedTransform),
        this.splitPointRadius,
        SPLIT_POINT_COLOR,
        undefined,
        true,
      );
    }
  }

  // Draw the floating points on top of the drag line in split filled subpath mode.
  private drawFloatingSplitFilledPathPreviewPoints(ctx: Context, camera: CanvasCamera) {
    if (
      !this.isActionMode ||
      this.actionSource === ActionSource.Animated ||
      !this.vectorLayer ||
      !this.blockLayerId ||
      !this.activePath
    ) {
      return;
    }
    if (this.actionMode !== ActionMode.SplitSubPaths || !this.shapeSplitter) {
      return;
    }
    const flattenedTransform = LayerUtil.getCanvasTransformForLayer(
      this.vectorLayer,
      this.blockLayerId,
    );
    const proj1 = this.shapeSplitter.getInitialProjectionOntoPath();
    if (proj1) {
      const proj2 = this.shapeSplitter.getFinalProjectionOntoPath();
      executeLabeledPoint(
        ctx,
        camera,
        applyGroupTransform(proj1.projection, flattenedTransform),
        this.splitPointRadius,
        SPLIT_POINT_COLOR,
        undefined,
        true,
      );
      if (this.shapeSplitter.willFinalProjectionOntoPathCreateSplitPoint()) {
        const endPoint = proj2
          ? applyGroupTransform(proj2.projection, flattenedTransform)
          : this.shapeSplitter.getLastKnownMouseLocation();
        if (endPoint) {
          executeLabeledPoint(
            ctx,
            camera,
            endPoint,
            this.splitPointRadius,
            SPLIT_POINT_COLOR,
            undefined,
            true,
          );
        }
      }
    } else {
      const projection = this.shapeSplitter.getCurrentProjectionOntoPath()?.projection;
      if (projection && projection.d < this.minSnapThreshold) {
        executeLabeledPoint(
          ctx,
          camera,
          applyGroupTransform(projection, flattenedTransform),
          this.splitPointRadius,
          SPLIT_POINT_COLOR,
          undefined,
          true,
        );
      }
    }
  }

  // Draws the pixel grid on top of the canvas content.
  private drawPixelGrid(ctx: Context, camera: CanvasCamera) {
    // Note that we draw the pixel grid in terms of physical pixels,
    // not viewport pixels.
    if (camera.scale > 4) {
      ctx.save();
      ctx.fillStyle = 'rgba(128, 128, 128, .25)';
      const { pixelRatio, deviceScale, viewport, panel } = camera;
      const origin = camera.viewportToDevice({ x: 0, y: 0 });
      // Only the lines in the panel, since a zoomed in artboard can have many more.
      const topLeft = camera.panelToViewport({ x: 0, y: 0 });
      const bottomRight = camera.panelToViewport({ x: panel.w, y: panel.h });
      const lastX = Math.min(viewport.w, bottomRight.x + 1);
      const lastY = Math.min(viewport.h, bottomRight.y + 1);
      for (let x = Math.max(1, Math.ceil(topLeft.x)); x < lastX; x++) {
        ctx.fillRect(
          origin.x + x * deviceScale - pixelRatio / 2,
          origin.y,
          pixelRatio,
          viewport.h * deviceScale,
        );
      }
      for (let y = Math.max(1, Math.ceil(topLeft.y)); y < lastY; y++) {
        ctx.fillRect(
          origin.x,
          origin.y + y * deviceScale - pixelRatio / 2,
          viewport.w * deviceScale,
          pixelRatio,
        );
      }
      ctx.restore();
    }
  }

  // Called by the CanvasController.
  onMouseDown(event: MouseEvent) {
    const mouseDown = this.mouseEventToViewportCoords(event);
    if (this.actionSource === ActionSource.Animated && !this.isActionMode) {
      // Detect layer selections.
      const hitLayer = this.hitTestForLayer(mouseDown);
      const isMetaOrShiftPressed =
        ShortcutService.isOsDependentModifierKey(event) || event.shiftKey;
      if (hitLayer) {
        this.layerTimelineService.selectLayer(hitLayer.id, !isMetaOrShiftPressed);
      } else if (!isMetaOrShiftPressed) {
        this.layerTimelineService.clearSelections();
      }
      return;
    }
    if (this.actionSource === ActionSource.Animated) {
      // Don't need to do anything for the animated canvas if we are in action mode.
      return;
    }
    if (this.actionMode === ActionMode.Selection) {
      this.selectionHelper?.onMouseDown(
        mouseDown,
        event.shiftKey || ShortcutService.isOsDependentModifierKey(event),
      );
    } else if (this.actionMode === ActionMode.PairSubPaths) {
      this.pairSubPathHelper?.onMouseDown(
        mouseDown,
        event.shiftKey || ShortcutService.isOsDependentModifierKey(event),
      );
    } else if (this.actionMode === ActionMode.SplitCommands) {
      this.segmentSplitter?.onMouseDown(mouseDown);
    } else if (this.actionMode === ActionMode.SplitSubPaths) {
      this.subPathSplitter?.onMouseDown(mouseDown);
    }
  }

  // Called by the CanvasController.
  onMouseMove(event: MouseEvent) {
    if (this.actionSource === ActionSource.Animated && !this.isActionMode) {
      return;
    }
    const mouseMove = this.mouseEventToViewportCoords(event);
    if (this.actionMode === ActionMode.Selection) {
      this.selectionHelper?.onMouseMove(mouseMove);
    } else if (this.actionMode === ActionMode.PairSubPaths) {
      this.pairSubPathHelper?.onMouseMove(mouseMove);
    } else if (this.actionMode === ActionMode.SplitCommands) {
      this.segmentSplitter?.onMouseMove(mouseMove);
    } else if (this.actionMode === ActionMode.SplitSubPaths) {
      this.subPathSplitter?.onMouseMove(mouseMove);
    }
  }

  // Called by the CanvasController.
  onMouseUp(event: MouseEvent) {
    if (this.actionSource === ActionSource.Animated && !this.isActionMode) {
      return;
    }
    const mouseUp = this.mouseEventToViewportCoords(event);
    if (this.actionMode === ActionMode.Selection) {
      this.selectionHelper?.onMouseUp(
        mouseUp,
        event.shiftKey || ShortcutService.isOsDependentModifierKey(event),
      );
    } else if (this.actionMode === ActionMode.PairSubPaths) {
      this.pairSubPathHelper?.onMouseUp(mouseUp);
    } else if (this.actionMode === ActionMode.SplitCommands) {
      this.segmentSplitter?.onMouseUp(mouseUp);
    } else if (this.actionMode === ActionMode.SplitSubPaths) {
      this.subPathSplitter?.onMouseUp(mouseUp);
    }
  }

  // Called by the CanvasController when the pointer stops hovering, or a gesture is canceled.
  onMouseLeave() {
    if (this.actionSource === ActionSource.Animated && !this.isActionMode) {
      return;
    }
    if (this.actionMode === ActionMode.Selection) {
      this.selectionHelper?.onMouseLeave();
    } else if (this.actionMode === ActionMode.PairSubPaths) {
      this.pairSubPathHelper?.onMouseLeave();
    } else if (this.actionMode === ActionMode.SplitCommands) {
      this.segmentSplitter?.onMouseLeave();
    } else if (this.actionMode === ActionMode.SplitSubPaths) {
      this.subPathSplitter?.onMouseLeave();
    }
    this.actionModeService.clearHover();
  }

  private mouseEventToViewportCoords(event: MouseEvent) {
    // The overlay's top left corner is the panel's.
    const { left, top } = this.canvas.getBoundingClientRect();
    const point = { x: event.clientX - left, y: event.clientY - top };
    return this.camera ? this.camera.panelToViewport(point) : point;
  }

  private hitTestForLayer(point: Point) {
    const root = this.vectorLayer;
    if (!root) {
      return undefined;
    }
    return hitTestLayer(root, point, {
      hiddenLayerIds: this.hiddenLayerIds,
      tolerance: this.minSnapThreshold,
    });
  }

  // NOTE: this should only be used in action mode
  performHitTest(mousePoint: Point, opts: HitTestOpts = {}) {
    const pathLayer = this.activePathLayer;
    if (!this.vectorLayer || !this.blockLayerId || !pathLayer?.pathData) {
      return NO_HIT;
    }
    const flattenedTransform = LayerUtil.getCanvasTransformForLayer(
      this.vectorLayer,
      this.blockLayerId,
    ).invert();
    if (!flattenedTransform) {
      // Do nothing if matrix is non-invertible.
      return NO_HIT;
    }
    const transformedMousePoint = MathUtil.transformPoint(mousePoint, flattenedTransform);
    let isPointInRangeFn: HitOptions['isPointInRangeFn'];
    if (!opts.noPoints) {
      isPointInRangeFn = (distance, cmd) => {
        const multiplyFactor = cmd?.isSplitPoint() ? SPLIT_POINT_RADIUS_FACTOR : 1;
        return distance <= this.mediumPointRadius * multiplyFactor;
      };
    }
    let isSegmentInRangeFn: HitOptions['isSegmentInRangeFn'];
    if (!opts.noSegments) {
      isSegmentInRangeFn = distance => {
        let maxDistance = opts.withExtraSegmentPadding ? this.minSnapThreshold : 0;
        if (pathLayer.isStroked()) {
          maxDistance = Math.max(maxDistance, (pathLayer as PathLayer).strokeWidth / 2);
        }
        return distance <= maxDistance;
      };
    }
    const findShapesInRange = pathLayer.isFilled() && !opts.noShapes;
    const restrictToSubIdx = opts.restrictToSubIdx;
    return pathLayer.pathData.hitTest(transformedMousePoint, {
      isPointInRangeFn,
      isSegmentInRangeFn,
      findShapesInRange,
      restrictToSubIdx,
    });
  }
}

function executeHighlights(
  ctx: Context,
  color: string,
  lineWidth: number,
  lineDash: number[] = [],
) {
  ctx.save();
  ctx.setLineDash(lineDash);
  ctx.lineCap = 'round';
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  ctx.stroke();
  ctx.restore();
}

// A split point's ring sits this many radiuses out from its border, so it doesn't rely on
// SPLIT_POINT_COLOR alone to read as different from a normal point.
const RING_RADIUS_FACTOR = 1.4;

// Draws a labeled point with optional text. A split point also gets a ring around it.
function executeLabeledPoint(
  ctx: Context,
  camera: CanvasCamera,
  point: Point,
  radius: number,
  color: string,
  text?: string,
  hasRing = false,
) {
  // Convert the point and the radius to physical pixel coordinates.
  // We do this to avoid fractional font sizes less than 1px, which
  // show up OK on Chrome but not on Firefox or Safari.
  point = camera.viewportToDevice(point);
  radius *= camera.deviceScale;

  ctx.save();
  ctx.beginPath();
  ctx.arc(point.x, point.y, radius * POINT_BORDER_FACTOR, 0, 2 * Math.PI, false);
  ctx.fillStyle = POINT_BORDER_COLOR;
  ctx.fill();

  ctx.beginPath();
  ctx.arc(point.x, point.y, radius, 0, 2 * Math.PI, false);
  ctx.fillStyle = color;
  ctx.fill();

  if (hasRing) {
    ctx.beginPath();
    ctx.arc(point.x, point.y, radius * RING_RADIUS_FACTOR, 0, 2 * Math.PI, false);
    ctx.strokeStyle = POINT_BORDER_COLOR;
    ctx.lineWidth = Math.max(1, radius * 0.15);
    ctx.stroke();
  }

  if (text) {
    ctx.beginPath();
    ctx.fillStyle = POINT_TEXT_COLOR;
    ctx.font = radius + 'px Roboto, Helvetica Neue, sans-serif';
    const width = ctx.measureText(text).width;
    // TODO: is there a better way to get the height?
    const height = ctx.measureText('o').width;
    ctx.fillText(text, point.x - width / 2, point.y + height / 2);
    ctx.fill();
  }
  ctx.restore();
}

// Takes a path point and transforms it so that its coordinates are in terms
// of the VectorLayer's viewport coordinates.
function applyGroupTransform(mousePoint: Point, transform: Matrix) {
  return MathUtil.transformPoint(mousePoint, transform);
}

interface HitTestOpts {
  readonly noPoints?: boolean;
  readonly noSegments?: boolean;
  readonly noShapes?: boolean;
  readonly restrictToSubIdx?: ReadonlyArray<number>;
  readonly withExtraSegmentPadding?: boolean;
}
